import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { useSignOut } from '../hooks/useSignOut'
import { AUTH_PRIMARY_BUTTON_CLASS, AuthScreen } from '../components/AuthScreen'
import { Button } from '../components/ui/Button'
import { QueryState } from '../components/ui/QueryStateCard'
import { ApiError, apiFetch } from '../lib/api'
import { AUTH_PATHS } from '../lib/routes'
import type { AuthAccount, SessionResponse } from '../lib/types'

const isUnauthorized = (err: unknown) => err instanceof ApiError && err.status === 401

const byName = (a: AuthAccount, b: AuthAccount) => a.name.localeCompare(b.name)

/**
 * Step 2 of the prototype's landing card (`#login-account-step`, MS-993;
 * ARCHITECTURE_DECISIONS.md §52, §54): after a Cognito sign-in the backend
 * holds a short-lived pending-login cookie, and this page picks the account
 * the session is then minted for. Same AuthScreen card as Login, no heading
 * of its own — only the subtitle changes, as the prototype's setLoginStep()
 * does:
 *
 * - more than one account: the Account dropdown (sorted by name, the first
 *   one preselected) and "Enter Subscription Portal";
 * - exactly one: entered straight away, the step is never shown;
 * - none: the "Access denied" badge and the denied copy, no button.
 *
 * The list is the active accounts the signed-in email holds a grant on
 * (MS-997 B7, ARCHITECTURE_DECISIONS.md §57). A 401 from either
 * call means the pending login is gone (expired, or never there), so the user
 * goes back to Login with the session-expired notice.
 *
 * "Sign out" sits under the loading, load-error, Access denied and account
 * form states: for a person with no account to pick, a choice that keeps
 * failing, or who wants to sign in with another credential. Not under the
 * expired state (already on its way to Login) nor the single-account wait
 * (it would be disabled throughout, as an entry is in flight; a failed entry
 * falls back to the form, which has it). Not in the prototype — a recorded
 * deviation, ARCHITECTURE_DECISIONS.md §58.
 */
export function SelectAccount() {
  const { t } = useTranslation()
  const { login, logout } = useAuth()
  const navigate = useNavigate()
  const selectId = useId()
  const [chosen, setChosen] = useState('')
  const autoEntered = useRef(false)
  const [signingOut, setSigningOut] = useState(false)
  const signOut = useSignOut(AUTH_PATHS.login)

  const accounts = useQuery({
    queryKey: ['auth', 'accounts'],
    queryFn: () => apiFetch<AuthAccount[]>('/api/v1/auth/accounts'),
    retry: false,
    gcTime: 0,
  })

  const select = useMutation({
    mutationFn: (accountId: string) =>
      apiFetch<SessionResponse>('/api/v1/auth/select-account', {
        method: 'POST',
        body: JSON.stringify({ accountId }),
      }),
    onSuccess: (session) => {
      login(session)
      navigate('/', { replace: true })
    },
  })

  const expired = isUnauthorized(accounts.error) || isUnauthorized(select.error)
  useEffect(() => {
    if (!expired) return
    logout('expired')
    navigate(AUTH_PATHS.login, { replace: true })
  }, [expired, logout, navigate])

  const sorted = useMemo(() => (accounts.data ? [...accounts.data].sort(byName) : []), [accounts.data])
  // The prototype preselects the first account; no placeholder option. A
  // choice that a refetch has taken off the list falls back to it too, so
  // Enter always posts the account on screen.
  const value = sorted.some((a) => a.id === chosen) ? chosen : (sorted[0]?.id ?? '')

  // A single account goes straight in (the prototype's accessibleAccounts()
  // length 1 → enterAccount). Once only: a failure shows the dropdown, with
  // the error, so it can be tried again.
  const single = sorted.length === 1 ? sorted[0].id : null
  const { mutate } = select
  useEffect(() => {
    // Never once Sign out is under way: a session minted alongside /logout
    // could outlive it and sign the person straight back in.
    if (!single || autoEntered.current || signingOut) return
    autoEntered.current = true
    mutate(single)
  }, [single, mutate, signingOut])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (value && !select.isPending && !signingOut) select.mutate(value)
  }

  // No confirm, unlike the AppShell's: there is no session here to lose.
  async function handleSignOut() {
    setSigningOut(true)
    await signOut()
  }

  // Centred by the AuthScreen card's own text-center.
  const signOutButton = (
    <Button variant="text" onClick={handleSignOut} disabled={signingOut || select.isPending} className="mt-4">
      {t('appShell.signOut')}
    </Button>
  )
  /** AuthScreen with the Sign out action under its content. */
  const screen = (props: { badgeLabel?: string; subtitle: string; error?: string }, children?: ReactNode) => (
    <AuthScreen {...props}>
      {children}
      {signOutButton}
    </AuthScreen>
  )

  const failed = select.isError && !expired
  const signInSubtitle = t('login.cognitoSubtitle')

  if (expired) return <AuthScreen subtitle={signInSubtitle} />

  if (accounts.isPending) {
    return screen({ subtitle: signInSubtitle }, <QueryState state="pending" />)
  }

  if (accounts.isError) {
    return screen(
      { subtitle: signInSubtitle },
      <QueryState state="error" message={t('selectAccount.loadFailed')} onRetry={() => !signingOut && accounts.refetch()} />
    )
  }

  if (sorted.length === 0) {
    return screen({ badgeLabel: t('accessDenied.title'), subtitle: t('accessDenied.message') })
  }

  if (single && !failed) {
    return (
      <AuthScreen subtitle={signInSubtitle}>
        <QueryState state="pending" />
      </AuthScreen>
    )
  }

  return screen(
    {
      // A failed single-account entry falls back to this form with one
      // option: "more than one account" would be untrue there.
      subtitle: single ? signInSubtitle : t('login.account.subtitle'),
      error: failed ? t('selectAccount.selectFailed') : undefined,
    },
    <form onSubmit={handleSubmit} className="text-left">
      <div className="mb-[14px]">
        <label htmlFor={selectId} className="block text-[11.5px] font-semibold text-[var(--text-secondary)] mb-[5px]">
          {t('login.account.label')}
        </label>
        {/* The prototype's `.login-account-step select` chevron: a 14px
            #6b7280 polyline inset 11px from the right, replacing the native
            arrow. An inline SVG (as components/ui/Select) rather than the
            prototype's data-URI background; it ignores the pointer, so a
            click on it still opens the select. */}
        <div className="relative">
          <select
            id={selectId}
            value={value}
            onChange={(e) => setChosen(e.target.value)}
            disabled={select.isPending || signingOut}
            className="w-full appearance-none pl-[11px] pr-[33px] py-[9px] font-[inherit] text-[13.5px] bg-[var(--bg-input)] text-[var(--text-primary)] border border-[var(--border)] rounded-[7px] outline-none cursor-pointer focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-subtle)] disabled:opacity-60"
          >
            {sorted.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <svg
            aria-hidden="true"
            data-testid="account-select-chevron"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="#6b7280"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="pointer-events-none absolute right-[11px] top-1/2 -translate-y-1/2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>
      <Button type="submit" disabled={select.isPending || signingOut} className={AUTH_PRIMARY_BUTTON_CLASS}>
        {t('login.account.enter')}
      </Button>
    </form>
  )
}
